/**
 * Interactive transcript that synchronises with an audio player.
 *
 * - Highlights the word currently being spoken (real-time via timeupdate).
 * - Clicking a word seeks the shared audio element to that word's start time (AC4).
 * - Words with a pronunciation issue are underlined in amber (AC2).
 * - Hovering an annotated word shows a tooltip with the issue and tip.
 * - Falls back to plain text when no word-level data is available.
 *
 * @module TranscriptWithTimestamps
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { WordTimestamp } from "@/types/simulation-results";
import {
  getPronunciationAnnotationId,
  getTextAnnotationId,
  type AnswerTextAnnotationSegment,
} from "./moduleAnswerReviewModel";

interface TranscriptWithTimestampsProps {
  readonly words: WordTimestamp[];
  readonly audioRef: React.RefObject<HTMLAudioElement>;
  readonly fallbackText?: string;
  readonly className?: string;
  readonly textAnnotationSegments?: readonly AnswerTextAnnotationSegment[];
  readonly answerId: string;
  readonly activeAnnotationId?: string | null;
}

interface WordTextRange {
  readonly start: number;
  readonly end: number;
}

const findWordFromCursor = (
  source: string,
  word: string,
  cursor: number,
): number => {
  const direct = source.indexOf(word, cursor);
  if (direct >= 0) return direct;
  return source.toLowerCase().indexOf(word.toLowerCase(), cursor);
};

const rangesOverlap = (a: WordTextRange, b: WordTextRange): boolean =>
  a.start < b.end && b.start < a.end;

const buildWordTextRanges = (
  source: string,
  words: readonly WordTimestamp[],
): WordTextRange[] => {
  let cursor = 0;

  return words.map((word) => {
    const start = findWordFromCursor(source, word.word, cursor);
    if (start < 0) {
      return {
        start: cursor,
        end: cursor,
      };
    }

    const end = start + word.word.length;
    cursor = end;
    return { start, end };
  });
};

export function TranscriptWithTimestamps({
  words,
  audioRef,
  fallbackText,
  className,
  textAnnotationSegments = [],
  answerId,
  activeAnnotationId,
}: TranscriptWithTimestampsProps) {
  const { t } = useTranslation();
  const transcriptWords = useMemo(() => words ?? [], [words]);
  const issueSegments = useMemo(
    () => textAnnotationSegments.filter((segment) => segment.state === "issue"),
    [textAnnotationSegments],
  );
  const wordTextRanges = useMemo(
    () =>
      buildWordTextRanges(
        fallbackText || transcriptWords.map((word) => word.word).join(" "),
        transcriptWords,
      ),
    [fallbackText, transcriptWords],
  );
  const [currentTime, setCurrentTime] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Track audio playback position for real-time word highlighting
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    return () => audio.removeEventListener("timeupdate", handleTimeUpdate);
  }, [audioRef]);

  if (transcriptWords.length === 0) {
    return fallbackText ? (
      <div className={cn("leading-relaxed", className)}>{fallbackText}</div>
    ) : null;
  }

  const handleWordClick = (word: WordTimestamp) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = word.start;
    // Start playing if paused so the seek is immediately audible
    if (audio.paused) {
      audio.play().catch(() => {
        /* handled by player error state */
      });
    }
  };

  const pronunciationIssueCount = transcriptWords.filter((w) => w.issue).length;

  return (
    <div className={cn("space-y-2", className)}>
      {/* Legend — only shown when there are pronunciation notes */}
      {pronunciationIssueCount > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-amber-400" />
            {t("simulation.results.reviewDialog.pronunciationNote", {
              defaultValue: "Pronunciation note",
            })}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full bg-primary/30" />
            {t("simulation.results.reviewDialog.nowPlaying", {
              defaultValue: "Now playing",
            })}
          </span>
          <span className="text-muted-foreground/60">
            {t("simulation.results.reviewDialog.clickWordToSeek", {
              defaultValue: "(click word to seek)",
            })}
          </span>
        </div>
      )}

      {/* Word-level transcript */}
      <div className="relative flex flex-wrap gap-x-1 gap-y-0.5 leading-relaxed">
        {transcriptWords.map((word, idx) => {
          const isActive = currentTime >= word.start && currentTime < word.end;
          const hasIssue = Boolean(word.issue);
          const wordTextRange = wordTextRanges[idx] ?? { start: 0, end: 0 };
          const textIssue = issueSegments.find((segment) =>
            rangesOverlap(wordTextRange, segment),
          );
          const hasTextIssue = Boolean(textIssue);
          const firstTextIssueWordIndex = textIssue
            ? wordTextRanges.findIndex((range) =>
                rangesOverlap(range, textIssue),
              )
            : -1;
          const isFirstTextIssueWord = firstTextIssueWordIndex === idx;
          const textAnnotationId = textIssue
            ? getTextAnnotationId(answerId, textIssue.annotationIndex ?? 1)
            : null;
          const pronunciationAnnotationId = getPronunciationAnnotationId(
            answerId,
            idx + 1,
          );
          const isTextAnnotationActive = Boolean(
            textAnnotationId && activeAnnotationId === textAnnotationId,
          );
          const isPronunciationAnnotationActive = Boolean(
            pronunciationAnnotationId &&
              activeAnnotationId === pronunciationAnnotationId,
          );
          const isHovered = hoveredIndex === idx;
          const pronunciationLabel = t(
            "simulation.results.reviewDialog.pronunciationNote",
            {
              defaultValue: "Pronunciation note",
            },
          );
          const correctionLabel = t(
            "simulation.results.reviewDialog.correction",
            {
              defaultValue: "Correction",
            },
          );
          const seekLabel = t("simulation.results.reviewDialog.seekToTime", {
            time: word.start.toFixed(1),
            defaultValue: `seek to ${word.start.toFixed(1)}s`,
          });
          let textIssueLabel = "";
          if (hasTextIssue) {
            textIssueLabel = textIssue?.correction
              ? `${correctionLabel}: ${textIssue.correction}`
              : correctionLabel;
          }
          const annotationLabel = [
            hasIssue ? `${pronunciationLabel}: ${word.issue}` : "",
            textIssueLabel,
          ]
            .filter(Boolean)
            .join("; ");

          return (
            <span
              key={idx}
              id={pronunciationAnnotationId ?? undefined}
              className="relative inline-block scroll-mt-32"
              data-annotation-id={pronunciationAnnotationId ?? undefined}
              data-annotation-state={hasIssue ? "pronunciation" : undefined}
            >
              <button
                id={
                  isFirstTextIssueWord
                    ? (textAnnotationId ?? undefined)
                    : undefined
                }
                type="button"
                onClick={() => handleWordClick(word)}
                onMouseEnter={() => setHoveredIndex(idx)}
                onMouseLeave={() => setHoveredIndex(null)}
                aria-label={
                  hasIssue || hasTextIssue
                    ? `${word.word} — ${annotationLabel}`
                    : `${word.word} — ${seekLabel}`
                }
                className={cn(
                  "rounded px-0.5 py-px font-ui text-sm transition-colors",
                  "cursor-pointer select-text",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
                  // Active word highlight
                  isActive && "bg-primary/15 text-foreground",
                  // Grammar/word choice correction from corrected_version markup
                  hasTextIssue &&
                    "border-b-2 border-destructive/70 bg-destructive/10 text-destructive",
                  // Pronunciation issue underline
                  hasIssue &&
                    !hasTextIssue &&
                    "underline decoration-amber-400 decoration-2 underline-offset-2",
                  (isTextAnnotationActive || isPronunciationAnnotationActive) &&
                    "ring-2 ring-primary/60 ring-offset-2 ring-offset-background",
                  // Hover state
                  !isActive && "hover:bg-muted/60",
                )}
                data-annotation-id={textAnnotationId ?? undefined}
                data-annotation-state={hasTextIssue ? "incorrect" : undefined}
              >
                {word.word}
              </button>

              {textIssue?.correction && isFirstTextIssueWord && (
                <span className="font-ui ml-1 inline-flex rounded bg-success/10 px-1 py-0.5 text-[11px] font-semibold text-success">
                  {t("simulation.results.reviewDialog.suggestedCorrection", {
                    value: textIssue.correction,
                    defaultValue: `→ ${textIssue.correction}`,
                  })}
                </span>
              )}

              {/* Tooltip for pronunciation issue */}
              {isHovered && hasIssue && (
                <div
                  ref={tooltipRef}
                  role="tooltip"
                  className={cn(
                    "absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2",
                    "w-max max-w-[220px] rounded-md border border-amber-200",
                    "bg-amber-50 px-2.5 py-1.5 shadow-md",
                    "pointer-events-none",
                  )}
                >
                  {word.issue && (
                    <p className="font-ui text-[11px] font-medium text-amber-900">
                      {word.issue}
                    </p>
                  )}
                  {word.tip && (
                    <p className="font-ui mt-0.5 text-[10px] text-amber-700">
                      {t("simulation.results.reviewDialog.tipWithValue", {
                        value: word.tip,
                        defaultValue: `Tip: ${word.tip}`,
                      })}
                    </p>
                  )}
                </div>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
